import IconButton from '@mui/material/IconButton';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import {
  GridCellEditStopParams,
  GridCellEditStopReasons,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowSelectionModel,
  GridValueGetter,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Site } from 'common/models/Site';
import { GetClientSites } from 'services/ClientsService';
import GridWithStateWrapper from '../GridWidthStateWrapper';
import { PreviewEntityType } from 'common/models/Previews/Previews';
import PreviewLoaderComponent from '../Previews/PreviewLoader';
import TagsManagementDialog from '../Dialogs/TagsManagementDialog';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/material/Box';
import ActionsDropDownButton from '../SummaryBars/Actions/ActionsDropsDownMenu';
import MenuItem from '@mui/material/MenuItem';
import { companyHasSinglePermission, userHasSinglePermission } from 'util/PermissionsUtils';
import { Permission } from 'common/models/Permissions';
import { GetPhoneLink } from 'util/CommonUtils';
import MergeSitesDialog from '../Dialogs/Sites/MergeSitesDialog';
import FloatCandidateDialog from '../Dialogs/Submissions/SitesFloatDialog';
import { SearchSites, SiteLookup, UpdateSiteCustomField } from 'services/SitesService';
import { ClientSearch } from 'common/models/Search/ClientSearch';
import {
  GetCustomFieldsByEntity_OnlyActive,
  GetPredefinedValues,
} from 'services/CustomFieldsService';
import { CustomField, CustomFieldPredefinedValue } from 'common/models/Configuration/CustomFields';
import {
  customFieldColsVisibility,
  mandatoryUdfEditValidator,
  numberUdfEditValidator,
} from 'util/GridUtils';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { TaggingReportFilter } from 'common/models/Reporting/AnalyticsFilterData';
import { GetTaggingReportModuleData } from 'services/DashboardService';

interface Props {
  source: 'client-record' | 'search' | 'lookup' | 'tagging-report';
  sourceId: number;
  lookupTerm?: string;
  searchData?: ClientSearch;
  filterData?: TaggingReportFilter;
  setRowCountHandler?: (count: number) => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
  gridName: string;
}

interface CustomFieldWithPredefinedValues extends CustomField {
  values?: CustomFieldPredefinedValue[];
}

const linkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };
const entityTypeId = 9;

const defaultHiddenCols: GridColumnVisibilityModel = {
  id: false,
  updatedByName: false,
  updatedDate: false,
  ...customFieldColsVisibility,
};

export default function SitesGridComponent({
  source,
  sourceId,
  lookupTerm = '',
  searchData,
  filterData,
  setRowCountHandler,
  loadingHandler,
  errorHandler,
  successHandler,
  gridName,
}: Props) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rows, setRows] = useState<Site[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showTagsPrevewNoDelay, setShowTagsPreviewNoDelay] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewEntityType | ''>('');
  const [previewRecordId, setPreviewRecordId] = useState(0);
  const [isPreviewTags, setIsPreviewTags] = useState(false);
  const [previewRecordName, setPreviewRecordName] = useState('');
  const [taggingRecords, setTaggingRecords] = useState<number[]>([]);
  const [mergeRecords, setMergeRecords] = useState<Site[]>([]);
  const [floatRecords, setFloatRecords] = useState<Site[]>([]);
  const [activeFields, setActiveFields] = useState<CustomFieldWithPredefinedValues[]>([]);
  const [editCellFieldKey, setEditCellFieldKey] = useState<keyof Site | null>(null);
  const apiRef = useGridApiRef();

  const userCanAddEditClients = useMemo(
    () =>
      companyHasSinglePermission(Permission.ClientsAddEdit) &&
      userHasSinglePermission(Permission.ClientsAddEdit),
    []
  );

  useEffect(() => {
    const getRows = async () => {
      loadingHandler && loadingHandler(true);
      let res: Site[] | null = null;
      if (source === 'client-record' && sourceId)
        res = await GetClientSites(sourceId, errorHandler);
      if (source === 'tagging-report' && filterData)
        res = await GetTaggingReportModuleData(entityTypeId, filterData, errorHandler);
      if (source === 'lookup' && lookupTerm)
        res = await SiteLookup(lookupTerm, 0, undefined, errorHandler);
      if (source === 'search' && searchData) res = await SearchSites(searchData, errorHandler);
      if (res) {
        setRowCountHandler && setRowCountHandler(res.length);
        setRows(res);
      }
      loadingHandler && loadingHandler(false);
    };
    getRows();
  }, [
    source,
    sourceId,
    lookupTerm,
    searchData,
    filterData,
    setRowCountHandler,
    loadingHandler,
    errorHandler,
  ]);

  useEffect(() => {
    const getActiveFields = async () => {
      const customFields = await GetCustomFieldsByEntity_OnlyActive(entityTypeId);
      if (customFields) {
        const udfDefinitions: CustomFieldWithPredefinedValues[] = [];
        for (let i = 0; i < customFields.length; i++) {
          const u = customFields[i];
          if (u.usePredefinedValues) {
            const vals = await GetPredefinedValues(u.id);
            if (vals) udfDefinitions.push({ ...u, values: vals });
            else udfDefinitions.push(u);
            continue;
          }

          udfDefinitions.push(u);
        }
        setActiveFields(udfDefinitions);
      }
    };
    getActiveFields();
  }, []);

  const columns = useMemo<GridColDef[]>(() => {
    const handlePreviewHover = (
      type: PreviewEntityType | '',
      id: number,
      isTags: boolean = false,
      recordName: string = '',
      noDelay: boolean = false
    ) => {
      setPreviewType(type);
      setPreviewRecordId(id);
      setIsPreviewTags(isTags);
      setPreviewRecordName(recordName);
      setIsPreviewOpen(true);
      setShowTagsPreviewNoDelay(noDelay);
    };

    const handlePreviewClose = () => {
      setIsPreviewOpen(false);
      setShowTagsPreviewNoDelay(false);
    };

    const tagsRenderer = (params: GridRenderCellParams) => {
      if (userCanAddEditClients) {
        return (
          <IconButton
            size="small"
            onMouseEnter={() => handlePreviewHover('site', params.row.id, true, params.row.name)}
            onMouseLeave={handlePreviewClose}
            onClick={() => setTaggingRecords([params.row.id])}
          >
            {params.value ? (
              <LocalOfferIcon style={{ color: '#f209a6' }} />
            ) : (
              <LocalOfferOutlinedIcon />
            )}
          </IconButton>
        );
      }

      return (
        <IconButton
          size="small"
          onMouseEnter={() => handlePreviewHover('site', params.row.id, true, params.row.name)}
          onMouseLeave={handlePreviewClose}
          onClick={() => handlePreviewHover('site', params.row.id, true, params.row.name, true)}
        >
          {params.value ? <LocalOfferIcon /> : <LocalOfferOutlinedIcon />}
        </IconButton>
      );
    };

    const linkToSiteRenderer = (params: GridRenderCellParams) => {
      return (
        <Link
          to={`/sites/${params.row.id}`}
          style={linkStyle}
          onMouseEnter={() => handlePreviewHover('site', params.row.id)}
          onMouseLeave={handlePreviewClose}
          target={source === 'search' ? '_blank' : '_self'}
        >
          {params.value}
        </Link>
      );
    };

    const linkToSiteContactsRenderer = (params: GridRenderCellParams) => {
      return (
        <Link target="_blank" to={`/sites/${params.row.id}?tab=Contacts`} style={linkStyle}>
          {params.value}
        </Link>
      );
    };

    const linkToClientRenderer = (params: GridRenderCellParams) => {
      const clientId = params.row.clientID as number;
      if (clientId) {
        return (
          <Link
            to={`/clients/${clientId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover('client', clientId)}
            onMouseLeave={handlePreviewClose}
          >
            {params.value}
          </Link>
        );
      }
      return params.value;
    };

    const linkToContactRenderer = (params: GridRenderCellParams) => {
      const contactId = params.row.primaryContact as number;
      if (contactId) {
        return (
          <Link
            to={`/contacts/${contactId}`}
            style={linkStyle}
            onMouseEnter={() => handlePreviewHover('contact', contactId)}
            onMouseLeave={handlePreviewClose}
          >
            {params.value}
          </Link>
        );
      }
      return params.value;
    };

    const phoneLinkRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return GetPhoneLink(params.value, true);
      }
      return params.value;
    };

    const addressRenderer = (params: GridRenderCellParams) => {
      return `${params.row.address1 ?? ''} ${params.row.address2 ?? ''} ${params.row.address3 ?? ''}`;
    };

    const dateValueGetter: GridValueGetter<Site, any, undefined, string> = (value) => {
      if (value) {
        const d = moment(value);
        if (d.isValid() && d.get('year') > 1) {
          return d.toDate();
        }
      }
    };

    const dateRenderer = (params: GridRenderCellParams) => {
      if (params.value) {
        return moment(params.value).format('DD MMM YYYY');
      }
    };

    const dateEditorRenderer = (params: GridRenderEditCellParams) => {
      const { id, value, field } = params;
      const dateUdfChangeHandler = (date: moment.Moment | null) => {
        const api = apiRef.current;
        if (api) {
          if (date) {
            if (date.isValid()) {
              const formatted = date.format('YYYY-MM-DD');
              api.setEditCellValue({ id, field, value: formatted });
            }
          } else api.setEditCellValue({ id, field, value: null });
        }
      };

      let v: moment.Moment | null = null;
      if (value) {
        const m = moment(value);
        if (m.isValid()) v = m;
      }

      return (
        <DatePicker
          value={v}
          onChange={dateUdfChangeHandler}
          slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
        />
      );
    };

    const decimalRenderer = (params: GridRenderCellParams) => {
      if (params.value || params.value === 0) {
        return Number(params.value).toFixed(2);
      }
    };

    const cols: GridColDef[] = [
      { headerName: 'ID', field: 'id', width: 75, renderCell: linkToSiteRenderer },
      { headerName: 'Name', field: 'name', width: 300, renderCell: linkToSiteRenderer },
      { headerName: 'Client', field: 'clientName', width: 300, renderCell: linkToClientRenderer },
      {
        headerName: 'Tags',
        field: 'tags',
        width: 70,
        headerAlign: 'center',
        align: 'center',
        type: 'boolean',
        renderCell: tagsRenderer,
      },
      {
        headerName: 'Phone',
        field: 'phone',
        headerAlign: 'center',
        align: 'center',
        width: 150,
        renderCell: phoneLinkRenderer,
      },
      {
        headerName: 'Address',
        field: 'address',
        headerAlign: 'center',
        align: 'center',
        width: 200,
        renderCell: addressRenderer,
      },
      { headerName: 'Suburb', field: 'suburb', headerAlign: 'center', align: 'center', width: 120 },
      { headerName: 'State', field: 'state', headerAlign: 'center', align: 'center', width: 120 },
      {
        headerName: 'Country',
        field: 'countryName',
        headerAlign: 'center',
        align: 'center',
        width: 120,
      },
      {
        headerName: 'Contacts',
        field: 'contacts',
        headerAlign: 'center',
        align: 'center',
        width: 80,
        renderCell: linkToSiteContactsRenderer,
      },
      {
        headerName: 'Primary Contact',
        field: 'primaryContactName',
        width: 200,
        renderCell: linkToContactRenderer,
      },
      { headerName: 'Status', field: 'status', headerAlign: 'center', align: 'center', width: 120 },
      {
        headerName: 'Updated By',
        field: 'updatedByName',
        headerAlign: 'center',
        align: 'center',
        width: 200,
      },
      {
        headerName: 'Updated Date',
        field: 'updatedDate',
        headerAlign: 'center',
        align: 'center',
        width: 110,
        type: 'date',
        valueGetter: dateValueGetter,
        renderCell: dateRenderer,
      },
    ];

    if (activeFields && activeFields.length > 0) {
      for (let i = 0; i < activeFields.length; i++) {
        const udf = activeFields[i];
        const udfNumber = udf.name.slice(13);
        const isString = udf.dataType === 'String';
        const isDecimal = udf.dataType === 'Decimal';
        const isDate = udf.dataType === 'DateTime';
        const isEditable = udf.editableViaGrids;
        const isDropdown = udf.usePredefinedValues;
        const isMandatory = udf.mandatory;

        if (isEditable && isDecimal) {
          cols.push({
            field: `customField${udfNumber}`,
            headerName: udf.agencyName,
            headerAlign: 'center',
            align: 'right',
            editable: true,
            preProcessEditCellProps: numberUdfEditValidator,
            renderCell: decimalRenderer,
          });
        } else if (isDate) {
          cols.push({
            field: `customField${udfNumber}`,
            headerName: udf.agencyName,
            headerAlign: 'center',
            editable: isEditable,
            valueGetter: dateValueGetter,
            renderCell: dateRenderer,
            renderEditCell: isEditable ? dateEditorRenderer : undefined,
            preProcessEditCellProps: isMandatory ? mandatoryUdfEditValidator : undefined,
          });
        } else if (isString && isDropdown && udf.values && udf.values.length > 0) {
          cols.push({
            field: `customField${udfNumber}`,
            headerName: udf.agencyName,
            headerAlign: 'center',
            editable: isEditable,
            valueOptions: udf.values.map((v) => v.value),
            type: 'singleSelect',
          });
        } else if (isEditable && isString) {
          cols.push({
            field: `customField${udfNumber}`,
            headerName: udf.agencyName,
            headerAlign: 'center',
            align: 'right',
            editable: true,
            preProcessEditCellProps: isMandatory ? mandatoryUdfEditValidator : undefined,
          });
        } else {
          cols.push({
            field: `customField${udfNumber}`,
            headerName: udf.agencyName,
            headerAlign: 'center',
            align: isDecimal ? 'right' : undefined,
            renderCell: isDecimal ? decimalRenderer : undefined,
          });
        }
      }
    }

    return cols;
  }, [activeFields, apiRef, userCanAddEditClients, source]);

  const mergeRecordsHandler = useCallback(() => {
    const selectedSites: Site[] = [];
    for (let i = 0; i < selectionModel.length; i++) {
      const siteId = selectionModel[i];
      const site = rows.find((s) => s.id === siteId.valueOf());
      if (site) selectedSites.push(site);
    }
    setMergeRecords(selectedSites);
  }, [rows, selectionModel]);

  const performFloatHandler = useCallback(() => {
    const selectedSites: Site[] = [];
    for (let i = 0; i < selectionModel.length; i++) {
      const siteId = selectionModel[i];
      const site = rows.find((s) => s.id === siteId.valueOf());
      if (site) {
        if (site.primaryContact === 0) {
          errorHandler && errorHandler('All selected sites must have a primary contact');
          return;
        }
        selectedSites.push(site);
      }
    }
    setFloatRecords(selectedSites);
  }, [rows, selectionModel, errorHandler]);

  const exportToExcel = useCallback(() => {
    const api = apiRef.current;
    if (api) api.exportDataAsExcel();
  }, [apiRef]);

  const gridActions = useMemo(() => {
    return (
      <>
        <ActionsDropDownButton label="Workflow">
          <MenuItem disabled={selectionModel.length === 0} onClick={performFloatHandler}>
            Float Candidate
          </MenuItem>
        </ActionsDropDownButton>
        {userCanAddEditClients && (
          <ActionsDropDownButton color="secondary" label="List Actions" ml="5px">
            {userCanAddEditClients && Boolean(sourceId) && (
              <MenuItem>
                <Link
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  to={`/sites/create?ClientID=${sourceId}`}
                >
                  Add Site
                </Link>
              </MenuItem>
            )}
            {userCanAddEditClients && <MenuItem onClick={exportToExcel}>Export</MenuItem>}
            {userCanAddEditClients && (
              <MenuItem disabled={selectionModel.length < 2} onClick={mergeRecordsHandler}>
                Merge Records
              </MenuItem>
            )}
            {userCanAddEditClients && (
              <MenuItem
                disabled={selectionModel.length === 0}
                onClick={() => setTaggingRecords(selectionModel as number[])}
              >
                Bulk Tag
              </MenuItem>
            )}
          </ActionsDropDownButton>
        )}
      </>
    );
  }, [
    userCanAddEditClients,
    sourceId,
    mergeRecordsHandler,
    performFloatHandler,
    exportToExcel,
    selectionModel,
  ]);

  const tagManagementSuccessHandler = useCallback(
    (message: string, recordIds: number[], finalTagCount: number) => {
      successHandler && successHandler(message);
      const api = apiRef.current;
      if (api) {
        const hasTags = finalTagCount > 0;
        api.updateRows(recordIds.map((id) => ({ id: id, tags: hasTags })));
      }
    },
    [apiRef, successHandler]
  );

  const cellEditHandler = useCallback((params: GridCellEditStopParams) => {
    if (params.reason === GridCellEditStopReasons.escapeKeyDown) return;
    setEditCellFieldKey(params.field as keyof Site);
  }, []);

  const processRowUpdate = useCallback(
    async (newRow: Site) => {
      if (editCellFieldKey && editCellFieldKey.startsWith('customField')) {
        const value = newRow[editCellFieldKey] as string;
        const n = +editCellFieldKey.slice(11);
        loadingHandler && loadingHandler(true);
        if ((n >= 16 && n <= 20) || (n >= 36 && n <= 40) || (n >= 56 && n <= 60)) {
          // IS DATE
          await UpdateSiteCustomField(+newRow.id, n, value ?? '0001-01-01', errorHandler);
        } else if (n >= 1 && n <= 60)
          await UpdateSiteCustomField(+newRow.id, n, value, errorHandler);
        loadingHandler && loadingHandler(false);
      }
      setEditCellFieldKey(null);
      return newRow;
    },
    [editCellFieldKey, errorHandler, loadingHandler]
  );

  return (
    <>
      <PreviewLoaderComponent
        open={isPreviewOpen}
        entityType={previewType}
        recordId={previewRecordId}
        isTagsPreview={isPreviewTags}
        showDelayMs={showTagsPrevewNoDelay ? 0 : undefined}
        titleOverride={previewRecordName}
        kioskToken={null}
      />
      <TagsManagementDialog
        open={taggingRecords.length > 0}
        closeHandler={() => setTaggingRecords([])}
        entityId={entityTypeId}
        recordIds={taggingRecords}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={tagManagementSuccessHandler}
      />
      <MergeSitesDialog
        open={mergeRecords.length > 1}
        sites={mergeRecords}
        closeHandler={() => setMergeRecords([])}
        errorHandler={errorHandler}
        loadingHandler={loadingHandler}
        successHandler={successHandler}
      />
      <FloatCandidateDialog
        open={floatRecords.length > 0}
        contactIds={floatRecords ? floatRecords.map((r) => r.primaryContact) : []}
        closeHandler={() => setFloatRecords([])}
      />
      {Boolean(gridActions) && (
        <Box pb="10px" ml="auto">
          {gridActions}
        </Box>
      )}
      <GridWithStateWrapper
        gridName={gridName}
        defaultViewModel={defaultHiddenCols}
        rows={rows}
        columns={columns}
        apiRef={apiRef}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(sm) => setSelectionModel(sm)}
        pagination
        onCellEditStop={cellEditHandler}
        processRowUpdate={processRowUpdate}
        pageSizeOptions={[100, 250, 500, 1000]}
      />
    </>
  );
}
